import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  backdropClasses,
  Modal,
  styled,
  SxProps,
  useMediaQuery,
} from '@mui/material';

import { ResponsiveImageProps } from '../types';
import { IconButton, Video, VideoContainer } from './video-base';
import YoutubeVideo from './youtube-video';

type Props = React.PropsWithChildren<{
  previewSources: {
    src: string;
    type: string;
  }[];
  mobilePreviewSources?: {
    src: string;
    type: string;
  }[];
  posterImage?: React.ReactElement<ResponsiveImageProps> | null;
  mobileBreakpoint?: number;
  youtubeVideo?: {
    id: string;
    title: string;
  };
  sx?: SxProps;
  className?: string;
}>;

export const AutoplayVideo = React.memo(
  ({
    previewSources,
    mobilePreviewSources,
    posterImage,
    youtubeVideo,
    sx,
    className = '',
    children,
  }: Props) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const [showFullVideo, setShowFullVideo] = useState(false);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const sources = useMemo(
      () =>
        isMobile && mobilePreviewSources
          ? mobilePreviewSources
          : previewSources,
      [isMobile, mobilePreviewSources, previewSources],
    );

    const onCloseFullVideo = useCallback(() => {
      setShowFullVideo(false);
    }, [setShowFullVideo]);

    const onShowFullVideo = useCallback(() => {
      setShowFullVideo(true);
    }, [setShowFullVideo]);

    useEffect(() => {
      if (videoRef.current) {
        // we need to reload the video if the sources changed
        videoRef.current.pause();
        const curTime = videoRef.current.currentTime;
        videoRef.current.load();
        videoRef.current.play();
        videoRef.current.currentTime = curTime;
      }
    }, [sources]);

    useEffect(() => {
      if (videoRef.current) {
        if (showFullVideo) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
      }
    }, [videoRef, showFullVideo]);

    const showImage = posterImage && sources.every((s) => !s.src);

    return (
      <StyledVideoContainer
        sx={{
          ...sx,
          backgroundColor: showImage ? undefined : 'var(--color-dark-coal)',
        }}
        className={className}
      >
        {!showImage ? (
          <Video ref={videoRef} autoPlay playsInline loop muted>
            {sources.map((s) => (
              <source key={s.src} src={s.src} type={s.type} />
            ))}
          </Video>
        ) : posterImage ? (
          <PosterImage>{posterImage}</PosterImage>
        ) : null}
        {youtubeVideo && (
          <>
            <IconButton
              onClick={onShowFullVideo}
              className="play-icon-desktop"
              variant="text"
              startIcon={<PlayIconDesktop />}
            />
            <IconButton
              onClick={onShowFullVideo}
              className="play-icon-mobile"
              variant="text"
              startIcon={<PlayIconMobile />}
            />
          </>
        )}

        {youtubeVideo && (
          <FullVideoModal open={showFullVideo} onClose={onCloseFullVideo}>
            <React.Fragment>
              <YoutubeVideo
                videoId={youtubeVideo.id}
                title={youtubeVideo.title}
                autoplay
              />

              <CloseButton
                startIcon={<LayerCloseIcon />}
                onClick={onCloseFullVideo}
              />
            </React.Fragment>
          </FullVideoModal>
        )}

        {children}
      </StyledVideoContainer>
    );
  },
);

export default AutoplayVideo;

const StyledVideoContainer = styled(VideoContainer)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    aspectRatio: '375/356',
  },
}));

const FullVideoModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '100px',
  color: 'white',

  iframe: {
    width: '100%',
    height: '100%',
  },

  [`.${backdropClasses.root}`]: {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  },

  [theme.breakpoints.down('md')]: {
    padding: '80px var(--margin)',
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  cursor: 'pointer',
  right: '40px',
  top: '40px',

  [theme.breakpoints.up('md')]: {
    transform: 'scale(1.5)',
  },

  [theme.breakpoints.down('md')]: {
    right: '30px',
    top: '30px',
  },

  [theme.breakpoints.down('sm')]: {
    right: 'var(--margin)',
    top: 'var(--margin)',
  },
}));

const PlayIconDesktop = () => (
  <svg
    width="70"
    height="70"
    viewBox="0 0 70 70"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="1"
      y="1"
      width="68"
      height="68"
      rx="34"
      stroke="white"
      strokeWidth="2"
    />
    <path
      d="M43.3984 35.0016L30.7984 42.2762L30.7984 27.7269L43.3984 35.0016Z"
      stroke="white"
      strokeWidth="2"
    />
  </svg>
);

const PlayIconMobile = () => (
  <svg
    width="50"
    height="50"
    viewBox="0 0 50 50"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="1"
      y="1"
      width="48"
      height="48"
      rx="24"
      stroke="white"
      strokeWidth="2"
    />
    <path
      d="M31 25L22 30.1962L22 19.8038L31 25Z"
      stroke="white"
      strokeWidth="2"
    />
  </svg>
);

const LayerCloseIcon = () => (
  <svg
    width="19"
    height="20"
    viewBox="0 0 19 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="1.41406"
      y="0.996094"
      width="24"
      height="2"
      transform="rotate(45 1.41406 0.996094)"
      fill="white"
    />
    <rect
      x="18.3828"
      y="2.41016"
      width="24"
      height="2"
      transform="rotate(135 18.3828 2.41016)"
      fill="white"
    />
  </svg>
);

const PosterImage = styled('div')({
  position: 'absolute',
  width: '100%',
  height: '100%',

  '.gatsby-image-wrapper, img': {
    height: '100%',
  },
});
